// Merge this customer into another (BL-029): pick the customer to keep, read what moves and what
// else happens (customers.previewMerge), type this customer's number, merge. Everything moves to the
// kept customer; this one stays only as a pointer to it. Owner and manager (customers.merge).

import { useEffect, useState } from 'react';
import { client } from '../api.ts';
import { Sheet, SheetButtons, useWrite } from '../shared/EditSheet.tsx';
import { refusal } from './customer-edit-model.ts';
import { mergeConfirmed, type MergePlan } from './merge-model.ts';

interface Found {
  id: string;
  number: string;
  displayName: string;
  city: string | null;
}

export function MergeCustomer({
  customer,
  onMerged,
  onClose,
}: {
  customer: { id: string; number: string; displayName: string; rowVersion?: number };
  /** Merged: open the kept customer, saying so. */
  onMerged: (keptId: string, said: string) => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<Found[]>([]);
  const [keep, setKeep] = useState<Found | null>(null);
  const [plan, setPlan] = useState<MergePlan | null>(null);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);
  const w = useWrite(`merge:${customer.id}:${keep?.id ?? ''}`);
  useEffect(() => {
    if (keep || !query.trim()) {
      setFound([]);
      return;
    }
    const t = setTimeout(() => {
      client
        .call<{ customers: Found[] }>('customers.list', { query: query.trim(), limit: 8 })
        .then((r) => setFound(r.customers.filter((c) => c.id !== customer.id)))
        .catch((e: unknown) => setError(refusal(e)));
    }, 200);
    return () => clearTimeout(t);
  }, [query, keep, customer.id]);
  useEffect(() => {
    setPlan(null);
    setTyped('');
    if (!keep) return;
    client
      .call<MergePlan>('customers.previewMerge', { keepId: keep.id, mergeId: customer.id })
      .then(setPlan)
      .catch((e: unknown) => setError(refusal(e)));
  }, [keep, customer.id]);
  const merge = async () => {
    if (!keep) return;
    const r = await w.run<MergePlan>('customers.merge', {
      input: {
        keepId: keep.id,
        mergeId: customer.id,
        confirmNumber: typed.trim(),
        ...(customer.rowVersion ? { mergeRowVersion: customer.rowVersion } : {}),
      },
    });
    if (r)
      onMerged(keep.id, `${customer.number} is merged into this customer. Everything of theirs is here now.`);
  };
  return (
    <Sheet title={`Merge ${customer.displayName} into another customer`} wide onClose={onClose}>
      <p className="muted">
        Everything of {customer.number}’s moves to the customer you keep: addresses, contacts, jobs, invoices,
        payments, memberships, cards and notes. {customer.number} stays only as a pointer to them. A merge
        can’t be undone.
      </p>
      {!keep ? (
        <>
          <label className="f">
            The customer to keep
            <input
              className="field"
              type="search"
              placeholder="Name, number, phone or address"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
          </label>
          {found.length > 0 && (
            <ul className="found" aria-label="Customers found">
              {found.map((c) => (
                <li key={c.id}>
                  <button type="button" className="row-btn" onClick={() => setKeep(c)}>
                    <span className="who-col">
                      <b>{c.displayName}</b> <span className="muted small">{c.number}</span>
                    </span>
                    {c.city && <span className="muted small">{c.city}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div className="merge-plan">
          <p>
            Keep <b>{keep.displayName}</b> ({keep.number}).{' '}
            <button type="button" className="btn quiet sm" onClick={() => setKeep(null)}>
              Pick another
            </button>
          </p>
          {plan && (
            <>
              <h4 className="h4">What moves to {keep.number}</h4>
              <p>{plan.moves.length ? plan.moves.join(', ') : 'Nothing but the customer record itself.'}</p>
              {plan.notes.length > 0 && (
                <ul className="merge-notes">
                  {plan.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              )}
              <label className="f">
                Type {customer.number} to merge it into {keep.number}
                <input
                  className="field"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                />
              </label>
            </>
          )}
        </div>
      )}
      {error && <p className="error small">{error}</p>}
      <SheetButtons
        w={w}
        label={keep ? `Merge into ${keep.number}` : 'Merge'}
        busyLabel="Merging…"
        disabled={!plan || !mergeConfirmed(typed, customer.number)}
        onSave={() => void merge()}
        onCancel={onClose}
      />
    </Sheet>
  );
}
